<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ title }}</h1>
      </div>
      <div class="header-actions">
        <template v-if="section === 'exceptions'">
          <router-link
            :to="{ name: 'SalonExceptionCreate', query: { type: exceptionType } }"
            class="btn btn-primary"
          >
            <i class="ri-add-line"></i> {{ exceptionCreateLabel }}
          </router-link>
        </template>
        <router-link v-else-if="createRoute" :to="{ name: createRoute }" class="btn btn-primary">
          <i class="ri-add-line"></i> {{ createLabel }}
        </router-link>
      </div>
    </div>

    <div class="page-container">
      <div
        v-if="section === 'exceptions'"
        class="sub-tabs"
        role="tablist"
        aria-label="休假與預約限制類型"
      >
        <button
          id="salon-closure-tab"
          type="button"
          role="tab"
          :aria-selected="exceptionType === 'closure'"
          aria-controls="salon-exceptions-panel"
          :class="{ active: exceptionType === 'closure' }"
          @click="changeExceptionType('closure')"
        >
          門市休假
        </button>
        <button
          id="salon-block-tab"
          type="button"
          role="tab"
          :aria-selected="exceptionType === 'block'"
          aria-controls="salon-exceptions-panel"
          :class="{ active: exceptionType === 'block' }"
          @click="changeExceptionType('block')"
        >
          預約限制
        </button>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <template v-else>
      <div v-if="section === 'settings'" class="booking-entry">
        <div v-if="bookingUrl" class="booking-link">
          <span class="booking-link-label">顧客選店預約連結</span>
          <div class="booking-link-field">
            <span class="booking-link-text">{{ bookingUrl }}</span>
            <button
              type="button"
              class="booking-copy"
              :class="{ copied: bookingCopied }"
              @click="copyBookingUrl"
            >
              <i :class="bookingCopied ? 'ri-check-line' : 'ri-file-copy-line'"></i>
              {{ bookingCopied ? '已複製' : '複製' }}
            </button>
          </div>
        </div>
        <p v-else class="booking-entry-hint">請先到 LINE 設定填寫共用入口 LIFF ID，才能產生顧客選店預約連結。</p>
      </div>

      <div
        id="salon-exceptions-panel"
        class="table-wrapper"
        :role="section === 'exceptions' ? 'tabpanel' : null"
        :aria-labelledby="section === 'exceptions' ? `salon-${exceptionType}-tab` : null"
      >
        <table v-if="section === 'stylists'">
          <thead><tr><th>頭像</th><th>代碼</th><th>名稱</th><th>門市</th><th>同時服務上限</th><th>狀態</th><th>操作</th></tr></thead>
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td>
                <div class="stylist-avatar">
                  <img v-if="item.avatarUrl" :src="item.avatarUrl" :alt="`${item.displayName}頭像`" />
                  <span v-else>{{ (item.displayName || '?').slice(0, 1) }}</span>
                </div>
              </td>
              <td><code class="stylist-code">{{ item.stylistCode }}</code></td>
              <td>{{ item.displayName }}</td>
              <td>{{ (item.stores || []).map(store => store.storeName).join('、') || '-' }}</td>
              <td>{{ item.defaultConcurrencyLimit || '依門市設定' }}</td>
              <td>{{ statusLabel(item.status) }}</td>
              <td class="action-buttons">
                <router-link :to="{ name: 'SalonStylistEdit', params: { id: item.id } }" class="btn btn-sm btn-primary">
                  <i class="ri-edit-line"></i> 編輯
                </router-link>
                <button v-if="item.status === 'active'" class="btn btn-sm btn-danger" @click="deactivateStylist(item)">停用</button>
              </td>
            </tr>
            <tr v-if="!items.length"><td colspan="7" class="empty-state">尚無設計師</td></tr>
          </tbody>
        </table>

        <table v-else-if="section === 'services'">
          <thead><tr><th>代碼</th><th>分類</th><th>名稱</th><th>價格</th><th>分鐘</th><th>狀態</th><th>操作</th></tr></thead>
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td>{{ item.serviceCode }}</td><td>{{ item.categoryName || '-' }}</td>
              <td>{{ item.name }}</td><td>{{ money(item.price) }}</td>
              <td>{{ item.durationMinutes }}</td><td>{{ statusLabel(item.status) }}</td>
              <td class="action-buttons">
                <router-link :to="{ name: 'SalonServiceEdit', params: { id: item.id } }" class="btn btn-sm btn-primary">
                  <i class="ri-edit-line"></i> 編輯
                </router-link>
                <button v-if="item.status === 'active'" class="btn btn-sm btn-danger" @click="deactivateService(item)">停用</button>
              </td>
            </tr>
            <tr v-if="!items.length"><td colspan="7" class="empty-state">尚無服務項目</td></tr>
          </tbody>
        </table>

        <table v-else-if="section === 'schedules'">
          <thead><tr><th>門市</th><th>排班模式</th><th>班表數</th><th>草稿</th><th>現行</th><th>待生效</th><th>操作</th></tr></thead>
          <tbody>
            <template v-for="store in items" :key="store.storeId">
              <tr>
                <td>{{ store.storeName }}</td>
                <td>
                  <div class="mode-tags">
                    <span
                      class="status-tag"
                      :class="activeScheduleMode(store) === 'weekly' ? 'is-weekly' : 'is-monthly'"
                    >
                      {{ activeScheduleMode(store) === 'weekly' ? '週排班' : '月排班' }}
                    </span>
                    <span
                      v-if="upcomingScheduleTag(store)"
                      class="status-tag is-pending"
                    >
                      {{ upcomingScheduleTag(store) }}
                    </span>
                  </div>
                  <div v-if="scheduleModeNotice(store)" class="mode-switch-notice">
                    <i class="ri-error-warning-line"></i>
                    {{ scheduleModeNotice(store) }}
                  </div>
                </td>
                <td>{{ store.schedules.length }}</td>
                <td>{{ scheduleLifecycleCount(store, 'draft') }}</td>
                <td>{{ scheduleLifecycleCount(store, 'current') }}</td>
                <td>{{ scheduleLifecycleCount(store, 'pending_effective') }}</td>
                <td class="action-buttons">
                  <router-link
                    :to="{ name: 'SalonScheduleCreate', query: { storeId: store.storeId } }"
                    class="btn btn-sm btn-primary"
                  >
                    <i class="ri-add-line"></i> 新增排班
                  </router-link>
                  <router-link
                    :to="{ name: 'SalonStoreSchedules', params: { storeId: store.storeId } }"
                    class="btn btn-sm btn-secondary"
                  >
                    查看班表
                  </router-link>
                </td>
              </tr>
            </template>
            <tr v-if="!items.length"><td colspan="7" class="empty-state">尚無門市</td></tr>
          </tbody>
        </table>

        <table v-else-if="section === 'exceptions'">
          <thead><tr><th>門市</th><th>名稱</th><th>模式</th><th>日期</th><th>時段</th><th>原因</th><th>狀態</th><th>操作</th></tr></thead>
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td>{{ item.storeName }}</td><td>{{ item.name }}</td>
              <td>
                <span class="status-tag" :class="ruleModeTone(item)">{{ ruleModeLabel(item) }}</span>
              </td>
              <td>{{ dateOnly(item.startDate) }}～{{ item.endDate ? dateOnly(item.endDate) : '永久' }}</td>
              <td>{{ item.isAllDay ? '整日' : `${shortTime(item.startTime)}–${shortTime(item.endTime)}` }}</td>
              <td>{{ item.reason || '-' }}</td><td>{{ statusLabel(item.status) }}</td>
              <td class="action-buttons">
                <router-link
                  :to="{ name: 'SalonExceptionEdit', params: { type: exceptionType, id: item.id } }"
                  class="btn btn-sm btn-primary"
                >
                  <i class="ri-edit-line"></i> 編輯
                </router-link>
                <button
                  v-if="item.status === 'active'"
                  class="btn btn-sm btn-danger"
                  @click="deactivateException(item)"
                >
                  {{ exceptionType === 'closure' ? '刪除' : '停用' }}
                </button>
              </td>
            </tr>
            <tr v-if="!items.length"><td colspan="8" class="empty-state">尚無規則</td></tr>
          </tbody>
        </table>

        <table v-else-if="section === 'settings'">
          <thead><tr><th>門市</th><th>開放預約</th><th>排班模式</th><th>時段單位</th><th>同時服務預設上限</th><th>可指定設計師</th><th>最遠預約</th><th>操作</th></tr></thead>
          <tbody>
            <tr v-for="item in items" :key="item.storeId">
              <td>{{ item.storeName }}</td>
              <td>
                <span class="status-tag" :class="item.bookingEnabled ? 'is-open' : 'is-closed'">
                  {{ item.bookingEnabled ? '已開放' : '未開放' }}
                </span>
              </td>
              <td>
                <div class="mode-tags">
                  <span
                    class="status-tag"
                    :class="activeScheduleMode(item) === 'weekly' ? 'is-weekly' : 'is-monthly'"
                  >
                    {{ activeScheduleMode(item) === 'weekly' ? '週排班' : '月排班' }}
                  </span>
                  <span
                    v-if="upcomingScheduleTag(item)"
                    class="status-tag is-pending"
                  >
                    {{ upcomingScheduleTag(item) }}
                  </span>
                </div>
                <div v-if="scheduleModeNotice(item)" class="mode-switch-notice">
                  <i class="ri-error-warning-line"></i>
                  {{ scheduleModeNotice(item) }}
                </div>
              </td>
              <td>{{ item.bookingSlotMinutes }} 分鐘</td>
              <td>{{ item.defaultConcurrencyLimit }} 人</td>
              <td>{{ item.allowStylistSelection ? '是' : '否' }}</td>
              <td>{{ item.maxFutureBookingDays }} 天</td>
              <td>
                <router-link :to="{ name: 'SalonSettingsEdit', params: { storeId: item.storeId } }" class="btn btn-sm btn-primary">
                  <i class="ri-edit-line"></i> 編輯
                </router-link>
              </td>
            </tr>
            <tr v-if="!items.length"><td colspan="8" class="empty-state">尚無門市設定</td></tr>
          </tbody>
        </table>
      </div>
      </template>
    </div>
  </div>
</template>

<script>
import { salonService } from '@/api/modules/salon.js';
import { storeService } from '@/api/modules/stores.js';
import { salonDateOnly } from '@/utils/salonDate.js';
import { showConfirm, showError, showSuccess } from '@/utils/swal.js';

const config = {
  stylists: { title: '設計師', createRoute: 'SalonStylistCreate', createLabel: '新增設計師' },
  services: { title: '服務項目', createRoute: 'SalonServiceCreate', createLabel: '新增服務項目' },
  schedules: { title: '排班', createRoute: 'SalonScheduleCreate', createLabel: '新增班表' },
  exceptions: { title: '休假與預約限制' },
  settings: { title: '預約設定' }
};

export default {
  name: 'SalonManagementList',
  props: {
    section: { type: String, required: true }
  },
  data() {
    return {
      loading: false,
      items: [],
      bookingCopied: false,
      exceptionType: this.$route.query.type === 'block' ? 'block' : 'closure'
    };
  },
  computed: {
    title() { return config[this.section]?.title || '美髮預約與排班'; },
    bookingUrl() {
      return this.items.find(item => item.bookingUrl)?.bookingUrl || '';
    },
    createRoute() { return config[this.section]?.createRoute || null; },
    createLabel() { return config[this.section]?.createLabel || ''; },
    exceptionCreateLabel() {
      return this.exceptionType === 'block' ? '新增預約限制' : '新增門市休假';
    }
  },
  watch: {
    section() { this.loadList(); }
  },
  mounted() {
    this.loadList();
  },
  methods: {
    async copyBookingUrl() {
      if (!this.bookingUrl) return;
      try {
        await navigator.clipboard.writeText(this.bookingUrl);
      } catch {
        const input = document.createElement('textarea');
        input.value = this.bookingUrl;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.select();
        const copied = document.execCommand('copy');
        document.body.removeChild(input);
        if (!copied) {
          await showError('複製失敗', '無法複製到剪貼簿，請手動複製');
          return;
        }
      }
      this.bookingCopied = true;
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => {
        this.bookingCopied = false;
      }, 2000);
    },
    activeScheduleMode(item) {
      if (item.pendingScheduleMode && item.scheduleModeEffectiveFrom
        && item.scheduleModeSwitchConfirmed
        && this.dateOnly(item.scheduleModeEffectiveFrom) <= salonDateOnly(new Date())) {
        return item.pendingScheduleMode;
      }
      return item.scheduleMode === 'weekly' ? 'weekly' : 'monthly';
    },
    upcomingScheduleTag(item) {
      if (!item.pendingScheduleMode || !item.scheduleModeEffectiveFrom) return '';
      const effectiveFrom = this.dateOnly(item.scheduleModeEffectiveFrom);
      if (!item.scheduleModeSwitchConfirmed) return '切換未完成';
      if (effectiveFrom > salonDateOnly(new Date())) {
        const nextLabel = item.pendingScheduleMode === 'monthly' ? '月排班' : '週排班';
        return `${effectiveFrom} 起改為${nextLabel}`;
      }
      return '';
    },
    scheduleModeNotice(item) {
      if (!item.pendingScheduleMode || !item.scheduleModeEffectiveFrom
        || item.scheduleModeSwitchConfirmed) return '';
      const label = item.pendingScheduleMode === 'monthly' ? '月排班' : '週排班';
      const effectiveFrom = this.dateOnly(item.scheduleModeEffectiveFrom);
      const readiness = item.modeSwitchReadiness;
      const reasons = [];
      const missingNames = (readiness?.missingSchedules || [])
        .map(stylist => stylist.stylistName)
        .filter(Boolean);
      if (missingNames.length) {
        reasons.push(`缺少 ${missingNames.join('、')} 的${label}`);
      }
      const conflictCount = readiness?.reservationConflicts?.length || 0;
      if (conflictCount) reasons.push(`有 ${conflictCount} 筆預約衝突待處理`);
      if (!reasons.length) reasons.push('切換條件尚未完成');
      const timing = effectiveFrom <= salonDateOnly(new Date())
        ? `原訂 ${effectiveFrom} 生效`
        : `預計 ${effectiveFrom} 生效`;
      return `${timing}；${reasons.join('；')}。完成後系統會自動切換。`;
    },
    scheduleLifecycleStatus(schedule) {
      if (schedule.status !== 'published') return schedule.status;
      const today = salonDateOnly(new Date());
      if (this.dateOnly(schedule.supersededFrom)
        && this.dateOnly(schedule.supersededFrom) <= today) return 'expired';
      if (schedule.scheduleType === 'monthly') {
        const currentMonth = today.slice(0, 7);
        if (schedule.yearMonth > currentMonth) return 'pending_effective';
        if (schedule.yearMonth < currentMonth) return 'expired';
        return 'current';
      }
      const effectiveFrom = this.dateOnly(schedule.effectiveFrom);
      const effectiveTo = this.dateOnly(schedule.effectiveTo);
      if (effectiveFrom > today) return 'pending_effective';
      if (effectiveTo && effectiveTo < today) return 'expired';
      return 'current';
    },
    scheduleLifecycleCount(store, status) {
      return store.schedules.filter(schedule => this.scheduleLifecycleStatus(schedule) === status).length;
    },
    async loadList() {
      this.loading = true;
      try {
        let response;
        if (this.section === 'stylists') response = await salonService.listStylists({ page: 1, pageSize: 100 });
        if (this.section === 'services') response = await salonService.listServices({ page: 1, pageSize: 100 });
        if (this.section === 'schedules') {
          const [schedulesResponse, storesResponse, settingsResponse] = await Promise.all([
            salonService.listSchedules({ page: 1, pageSize: 100 }),
            storeService.getStores({ page: 1, pageSize: 100, status: 'active' }),
            salonService.listSettings()
          ]);
          const schedules = schedulesResponse?.data?.items || [];
          const settings = settingsResponse?.data || [];
          const settingsByStore = new Map(settings.map(item => [item.storeId, item]));
          this.items = (storesResponse?.data?.items || []).map(store => ({
            storeId: store.id,
            storeName: store.storeName,
            scheduleMode: 'monthly',
            pendingScheduleMode: null,
            scheduleModeEffectiveFrom: null,
            scheduleModeSwitchConfirmed: false,
            ...settingsByStore.get(store.id),
            schedules: schedules.filter(schedule => schedule.storeId === store.id)
          }));
          if (this.items.length === 1) {
            await this.$router.replace({
              name: 'SalonStoreSchedules',
              params: { storeId: this.items[0].storeId }
            });
          }
          return;
        }
        if (this.section === 'settings') {
          const [settingsResponse, storesResponse] = await Promise.all([
            salonService.listSettings(),
            storeService.getStores({ page: 1, pageSize: 100 })
          ]);
          const settings = settingsResponse?.data || [];
          const settingsByStore = new Map(settings.map(item => [item.storeId, item]));
          this.items = (storesResponse?.data?.items || []).map(store => ({
            storeId: store.id,
            storeName: store.storeName,
            bookingEnabled: false,
            scheduleMode: 'monthly',
            defaultConcurrencyLimit: 1,
            bookingSlotMinutes: 30,
            allowStylistSelection: true,
            maxFutureBookingDays: 90,
            ...settingsByStore.get(store.id)
          }));
          return;
        }
        if (this.section === 'exceptions') {
          response = this.exceptionType === 'closure'
            ? await salonService.listClosures({ page: 1, pageSize: 100, status: 'active' })
            : await salonService.listBlocks({ page: 1, pageSize: 100 });
        }
        this.items = response?.data?.items || response?.data || [];
      } catch (error) {
        await this.handleError(error, '資料載入失敗');
      } finally {
        this.loading = false;
      }
    },
    changeExceptionType(type) {
      if (this.exceptionType === type) return;
      this.exceptionType = type;
      this.$router.replace({ name: 'SalonExceptions', query: { type } });
      this.loadList();
    },
    async deactivateStylist(item) {
      if (!await showConfirm('停用設計師', `確定停用「${item.displayName}」？`)) return;
      await this.runAction(() => salonService.deactivateStylist(item.id), '設計師已停用');
    },
    async deactivateService(item) {
      if (!await showConfirm('停用服務', `確定停用「${item.name}」？`)) return;
      await this.runAction(() => salonService.deactivateService(item.id), '服務項目已停用');
    },
    async deactivateException(item) {
      const isClosure = this.exceptionType === 'closure';
      const confirmed = await showConfirm(
        isClosure ? '刪除門市休假' : '停用規則',
        isClosure ? `確定刪除「${item.name}」？刪除後不再影響預約。` : `確定停用「${item.name}」？`
      );
      if (!confirmed) return;
      const action = isClosure
        ? () => salonService.deactivateClosure(item.id)
        : () => salonService.deactivateBlock(item.id);
      await this.runAction(action, isClosure ? '門市休假已刪除' : '規則已停用');
    },
    async runAction(action, message) {
      try {
        await action();
        await showSuccess('操作成功', message);
        await this.loadList();
      } catch (error) {
        await this.handleError(error, '操作失敗');
      }
    },
    statusLabel(status) {
      return {
        active: '啟用', inactive: '停用', draft: '草稿', published: '已發布',
        archived: '已封存', confirmed: '已確認', checked_in: '已報到',
        in_service: '服務中', completed: '已完成', cancelled: '已取消', no_show: '未到'
      }[status] || status;
    },
    ruleModeTone(item) {
      if (item.recurrenceType === 'weekly') return 'is-recurring-weekly';
      if (item.recurrenceType === 'monthly') return 'is-recurring-monthly';
      return this.dateOnly(item.startDate) === this.dateOnly(item.endDate) ? 'is-single' : 'is-range';
    },
    ruleModeLabel(item) {
      if (item.recurrenceType === 'weekly') {
        const labels = ['日', '一', '二', '三', '四', '五', '六'];
        const weekdays = (item.daysOfWeek || []).map(day => labels[Number(day)]).filter(Boolean).join('、');
        return `每週${weekdays ? `（${weekdays}）` : ''}`;
      }
      if (item.recurrenceType === 'monthly') {
        const monthDays = (item.daysOfMonth || []).map(Number).sort((a, b) => a - b).join('、');
        return `每月${monthDays ? `（${monthDays} 日）` : ''}`;
      }
      return this.dateOnly(item.startDate) === this.dateOnly(item.endDate) ? '單日' : '多日';
    },
    money(value) {
      if (value == null || value === '') return '未設定';
      return `NT$ ${Number(value).toLocaleString('zh-TW')}`;
    },
    dateOnly(value) { return salonDateOnly(value); },
    shortTime(value) { return value ? String(value).slice(0, 5) : ''; },
    async handleError(error, fallback) {
      const message = error.response?.data?.message || error.message || fallback;
      await showError(fallback, message);
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-outer > .page-container {
  padding-top: 56px;
}

.sub-tabs {
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  overflow-x: auto;
}

.sub-tabs button {
  background: transparent;
  border: 0;
  border-bottom: 3px solid transparent;
  color: #64748b;
  cursor: pointer;
  font-weight: 600;
  margin-bottom: -1px;
  padding: 11px 18px;
  white-space: nowrap;
}

.sub-tabs button:hover {
  color: #4338ca;
}

.sub-tabs button.active {
  border-bottom-color: #4f46e5;
  color: #4338ca;
}

.booking-entry { margin-bottom: 16px; }
.booking-entry-hint { color: #64748b; font-size: 13px; margin: 0; }
.booking-link { display: grid; gap: 6px; min-width: 0; }
.booking-link-label { color: #64748b; font-size: 12px; }
.booking-link-field {
  align-items: center;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  gap: 8px;
  min-width: 0;
  padding: 4px 4px 4px 12px;
}
.booking-link-text {
  color: #334155;
  flex: 1;
  font-size: 13px;
  line-height: 1.4;
  min-width: 0;
  overflow-wrap: anywhere;
}
.booking-copy {
  align-items: center;
  background: #eef2ff;
  border: 0;
  border-radius: 6px;
  color: #4338ca;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  gap: 4px;
  height: 32px;
  padding: 0 10px;
}
.booking-copy:hover { background: #e0e7ff; }
.booking-copy.copied { background: #ecfdf5; color: #047857; }

.table-wrapper table {
  min-width: 980px;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.stylist-avatar {
  align-items: center;
  background: #eef2ff;
  border-radius: 50%;
  color: #4338ca;
  display: inline-flex;
  font-weight: 700;
  height: 44px;
  justify-content: center;
  overflow: hidden;
  width: 44px;
}

.stylist-avatar img {
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.stylist-code {
  color: #334155;
  white-space: nowrap;
}

.status-tag {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  color: #475569;
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  padding: 3px 9px;
  white-space: nowrap;
}

.status-tag.is-open {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #047857;
}

.status-tag.is-closed {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #64748b;
}

.status-tag.is-monthly {
  background: #eef2ff;
  border-color: #c7d2fe;
  color: #4338ca;
}

.status-tag.is-weekly {
  background: #ecfeff;
  border-color: #a5f3fc;
  color: #0e7490;
}

.status-tag.is-single {
  background: #eef2ff;
  border-color: #c7d2fe;
  color: #4338ca;
}

.status-tag.is-range {
  background: #ecfeff;
  border-color: #a5f3fc;
  color: #0e7490;
}

.status-tag.is-recurring-weekly {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #c2410c;
}

.status-tag.is-recurring-monthly {
  background: #f5f3ff;
  border-color: #ddd6fe;
  color: #6d28d9;
}

.status-tag.is-pending {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #c2410c;
}

.mode-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.mode-switch-notice {
  color: #b45309;
  font-size: 12px;
  line-height: 1.5;
  margin-top: 4px;
  max-width: 360px;
}

.mode-switch-notice i {
  margin-right: 3px;
}

</style>
